import Swal from 'sweetalert2';
import { getButtonClassName } from './Button';

// Centralized SweetAlert2 instance with unified defaults and Tailwind classes
const swal = Swal.mixin({
  buttonsStyling: false,
  width: 480,
  padding: '0',
  showConfirmButton: true,
  customClass: {
    popup: 'swal-custom-popup rounded-lg shadow-lg',
    title: 'swal-custom-title',
    closeButton: 'swal-custom-close',
    htmlContainer: 'swal-custom-content',
    actions: 'swal-custom-actions flex gap-3 justify-end',
    confirmButton: getButtonClassName({ variant: 'primary', size: 'md' }),
    cancelButton: getButtonClassName({ variant: 'secondary', size: 'md' }),
  },
});

function buildConfirmHtml(description: string, bodyHtml?: string) {
  const safe = description || '';
  const body = bodyHtml || `<div class="text-center text-gray-600 text-sm leading-relaxed">${safe}</div>`;

  return (
    `<div class="py-6 px-5 rounded-xl bg-[#F1F7FD]">
      <div class="w-full flex items-center justify-center mb-3">
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="6" height="6" rx="1.5" fill="#1D5176"/>
          <circle cx="16" cy="6" r="3" fill="#1D5176"/>
          <path d="M6 14l3 5H3l3-5z" fill="#1D5176"/>
          <path d="M14 14h6v6h-6z" fill="#1D5176"/>
        </svg>
      </div>
      ${body}
    </div>`
  );
}

export interface ConfirmOptions {
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  width?: number;
}

// Reusable SweetAlert2 confirmation modal styled to match product guidelines
export async function showConfirmDeleteModal(params?: { entityLabel?: string } & ConfirmOptions): Promise<boolean> {
  const entity = (params?.entityLabel || 'Item').trim();
  const title = params?.title || `Delete ${entity}`;
  const description = params?.description || `This action will remove permanently the current ${entity.toLowerCase()} and its information, do you want to proceed?`;
  const confirmText = params?.confirmText || 'Continue';
  const cancelText = params?.cancelText || 'Cancel';
  const width = params?.width || 480;

  const result = await swal.fire({
    title,
    html: buildConfirmHtml(description),
    showCloseButton: true,
    showCancelButton: true,
    reverseButtons: true, // Cancel left, Confirm right
    cancelButtonText: cancelText,
    confirmButtonText: confirmText,
    width,
  });
  return Boolean(result.isConfirmed);
}

// Generic confirm modal (for non-delete confirmations)
export async function showConfirmModal(options: ConfirmOptions & { icon?: 'warning' | 'question' }): Promise<boolean> {
  const result = await swal.fire({
    title: options.title || 'Are you sure?',
    html: buildConfirmHtml(options.description || ''),
    showCloseButton: true,
    showCancelButton: true,
    reverseButtons: true,
    cancelButtonText: options.cancelText || 'Cancel',
    confirmButtonText: options.confirmText || 'Continue',
    width: options.width || 480,
  });
  return Boolean(result.isConfirmed);
}

// Error/info/success helpers with unified styling
export async function showErrorModal(options: { title?: string; description?: string; html?: string; confirmText?: string; width?: number; timer?: number; showConfirmButton?: boolean }) {
  return await swal.fire({
    title: options.title || 'Something went wrong',
    html: buildConfirmHtml(options.description || '', options.html),
    showCloseButton: true,
    confirmButtonText: options.confirmText || 'OK',
    width: options.width || 480,
    showCancelButton: false,
    timer: options.timer,
    showConfirmButton: options.showConfirmButton !== undefined ? options.showConfirmButton : true,
  });
}

export async function showSuccessModal(options: { title?: string; description?: string; confirmText?: string; width?: number; timer?: number; showConfirmButton?: boolean }) {
  return await swal.fire({
    title: options.title || 'Success',
    html: buildConfirmHtml(options.description || ''),
    showCloseButton: true,
    confirmButtonText: options.confirmText || 'OK',
    width: options.width || 480,
    showCancelButton: false,
    timer: options.timer,
    showConfirmButton: options.showConfirmButton !== undefined ? options.showConfirmButton : true,
  });
}

export async function showInfoModal(options: { title?: string; description?: string; confirmText?: string; width?: number; timer?: number; showConfirmButton?: boolean }) {
  return await swal.fire({
    title: options.title || 'Info',
    html: buildConfirmHtml(options.description || ''),
    showCloseButton: true,
    confirmButtonText: options.confirmText || 'OK',
    width: options.width || 480,
    showCancelButton: false,
    timer: options.timer,
    showConfirmButton: options.showConfirmButton !== undefined ? options.showConfirmButton : true,
  });
}

// Loading modal with spinner
export function showLoadingModal(options?: { title?: string; message?: string }): void {
  const title = options?.title || 'Publishing Changes';
  const message = options?.message || 'Please wait while your changes are being published...';
  
  const spinnerHtml = `
    <div class="py-6 px-5 rounded-xl bg-[#F1F7FD]">
      <div class="w-full flex items-center justify-center mb-4">
        <div class="w-12 h-12 border-4 border-gray-200 border-t-[#1D74D8] rounded-full animate-spin"></div>
      </div>
      <div data-loading-message class="text-center text-gray-600 text-sm leading-relaxed">${message}</div>
    </div>
  `;

  swal.fire({
    title,
    html: spinnerHtml,
    showConfirmButton: false,
    showCancelButton: false,
    showCloseButton: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
    width: 480,
  });
}

// Update the message of an already-open loading modal, for long-running progress.
export function updateLoadingModalMessage(message: string): void {
  const target = swal.getHtmlContainer()?.querySelector('[data-loading-message]');
  if (target) target.textContent = message;
}

// Close the loading modal
export function closeLoadingModal(): void {
  swal.close();
}

// Export the configured instance in case it's needed elsewhere
export { swal as configuredSwal };


